import React, { useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';

import { buildSetAgeGroupBucket, getAgeGroupBucketSettings } from '../../../lib/services/age-group.service';
import { useIsMounted } from '../../../lib/hooks/use-is-mounted';
import { ConfirmDialog } from '../../_core/_ui/confirm-dialog.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { NO_BUCKET } from './age-group-bucket-settings.constants';
import { messages } from './age-group-bucket-settings.messages';
import { retiredBucketFor, statusFor } from './age-group-bucket-settings.helpers';

interface IAgeGroupBucketSettingsProps {
  assessmentId?: string | null;
}

export const AgeGroupBucketSettings: React.FC<IAgeGroupBucketSettingsProps> = ({ assessmentId }) => {
  const { bucketSettings, loading, error } = getAgeGroupBucketSettings(assessmentId);
  const { setAgeGroupBucket } = buildSetAgeGroupBucket();
  const [saveError, setSaveError] = useState<React.ReactNode>(null);
  const [pendingClear, setPendingClear] = useState<IAgeGroupBucketSetting | null>(null);
  // The ref blocks a repeat within the same tick; the state disables the row's Select while it saves.
  const inFlightIds = useRef(new Set<string>());
  const [savingIds, setSavingIds] = useState<string[]>([]);
  const isMounted = useIsMounted();

  const { options, ageGroups } = bucketSettings;

  const saveBucket = (ageGroup: IAgeGroupBucketSetting, value: string | null) => {
    if (inFlightIds.current.has(ageGroup._id)) return;
    inFlightIds.current.add(ageGroup._id);
    setSavingIds(ids => [...ids, ageGroup._id]);
    setSaveError(null);
    setAgeGroupBucket(ageGroup._id, value)
      .catch(err => {
        console.error('setAgeGroupBucket failed:', err);
        if (isMounted()) setSaveError(<IntlMsg {...messages.saveError} values={{ name: ageGroup.name }} />);
      })
      .finally(() => {
        inFlightIds.current.delete(ageGroup._id);
        if (isMounted()) setSavingIds(ids => ids.filter(id => id !== ageGroup._id));
      });
  };

  // Clearing a bucket retires the weights it seeded on the server, so it is confirmed first.
  const handleChange = (ageGroup: IAgeGroupBucketSetting, value: string) => {
    if (value === NO_BUCKET && ageGroup.bucket) {
      setPendingClear(ageGroup);
      return;
    }
    saveBucket(ageGroup, value === NO_BUCKET ? null : value);
  };

  const handleConfirmClear = () => {
    if (pendingClear) saveBucket(pendingClear, null);
    setPendingClear(null);
  };

  // A skipped query reports loading, so a missing assessment is named before the loading line.
  if (!assessmentId) {
    return <Typography color="textSecondary"><IntlMsg {...messages.noAssessment} /></Typography>;
  }
  if (loading && !ageGroups.length) return <Typography><IntlMsg {...messages.loading} /></Typography>;
  if (error) return <Typography color="error"><IntlMsg {...messages.loadError} /></Typography>;
  if (!ageGroups.length) {
    return <Typography color="textSecondary"><IntlMsg {...messages.noAgeGroups} /></Typography>;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary"><IntlMsg {...messages.intro} /></Typography>
      </Grid>
      {!options.length && (
        <Grid item xs={12}>
          <Typography variant="body2" color="error"><IntlMsg {...messages.noOptions} /></Typography>
        </Grid>
      )}
      {saveError && (
        <Grid item xs={12}>
          <Typography variant="body2" color="error">{saveError}</Typography>
        </Grid>
      )}
      {/* The server orders rows newest first; re-sorting here would fork that rule. */}
      {ageGroups.map(ageGroup => {
        const status = statusFor(ageGroup);
        const retired = retiredBucketFor(ageGroup, options);
        return (
          <Grid item xs={12} key={ageGroup._id}>
            <Card variant="outlined">
              <Box display="flex" alignItems="center" justifyContent="space-between" p={1.5}>
                <Box minWidth={0}>
                  <Typography variant="subtitle2" noWrap>{ageGroup.name}</Typography>
                  <Typography variant="caption" color="textSecondary" noWrap>
                    {ageGroup.assessmentName}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" flexShrink={0} ml={1}>
                  {status && (
                    <Box mr={1}>
                      <Chip
                        size="small"
                        color={status.color}
                        label={<IntlMsg {...status.label} values={status.values} />}
                      />
                    </Box>
                  )}
                  <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                    <InputLabel id={`bucket-${ageGroup._id}`}>
                      <IntlMsg {...messages.bucketLabel} />
                    </InputLabel>
                    <Select
                      labelId={`bucket-${ageGroup._id}`}
                      label={<IntlMsg {...messages.bucketLabel} />}
                      value={ageGroup.bucket || NO_BUCKET}
                      disabled={savingIds.includes(ageGroup._id)}
                      onChange={e => handleChange(ageGroup, String(e.target.value ?? NO_BUCKET))}
                    >
                      <MenuItem value={NO_BUCKET}>
                        <IntlMsg {...messages.noBucket} />
                      </MenuItem>
                      {/* Readable so the admin can see what is pinned, disabled so it can't be re-chosen. */}
                      {retired && (
                        <MenuItem value={retired} disabled>
                          <IntlMsg {...messages.retiredBucket} values={{ bucket: retired }} />
                        </MenuItem>
                      )}
                      {options.map(option => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Card>
          </Grid>
        );
      })}
      <ConfirmDialog
        open={!!pendingClear}
        title={<IntlMsg {...messages.confirmClearTitle} values={{ name: pendingClear?.name }} />}
        message={(
          <IntlMsg
            {...messages.confirmClearBody}
            values={{ name: pendingClear?.name, bucket: pendingClear?.bucket }}
          />
        )}
        confirmLabel={<IntlMsg {...messages.confirmClear} />}
        cancelLabel={<IntlMsg {...messages.cancel} />}
        onConfirm={handleConfirmClear}
        onCancel={() => setPendingClear(null)}
        destructive
      />
    </Grid>
  );
};
